import React, { useCallback, useState } from "react";
import { theme } from "antd";
import { useNotification, useTranslate } from "@refinedev/core";
import { PlusIcon } from "@phosphor-icons/react";
import {
  PlaybookCreationCard,
  PlaybookDetailsModal,
  Spinner,
} from "@libertify/ui";
import type { CarouselPlaybook } from "../types";
import { usePlaybookRecord } from "../hooks/usePlaybookRecord";
import { PlaybookCardMenu, PlaybookCardMenuStyles } from "./PlaybookCardMenu";
import { ConfirmRemovalModal } from "./ConfirmRemovalModal";
import { RenamePlaybookModal } from "./RenamePlaybookModal";
import {
  Wrap,
  StepEyebrow,
  Title,
  Slider,
  Slide,
  StateWrap,
  GhostCard,
  GhostIcon,
  GhostTitle,
  GhostDesc,
} from "./PlaybookCarousel.styles";
import { useSession } from "hooks/useSession";
import { LIBERTIFY_DEFAULT_PLAYBOOK_ORG_ID } from "../helpers/consts";

interface PlaybookCarouselProps {
  selectedId: string | null;
  onSelect: (playbook: CarouselPlaybook) => void;
  /** Edit this org's own playbook in place — the ⋮ menu's Edit. */
  onEditPlaybook: (playbook: CarouselPlaybook) => void;
  /** Configure an experience from the playbook — the card's Customize. Never
   *  writes to the playbook: the edits become a copy at create time. */
  onCustomizePlaybook: (playbook: CarouselPlaybook) => void;
  /** A playbook is gone — lets the page drop it if it was the selection. */
  onPlaybookDeleted: (id: string) => void;
  /** Start a from-scratch experience (no playbook). */
  onBuildYourOwn: () => void;
  isLoading: boolean;
  playbooks: CarouselPlaybook[];
}

/** Which of the ⋮ menu's modals is open, and for which playbook. */
type MenuAction = {
  kind: "delete" | "rename";
  playbook: CarouselPlaybook;
};

/**
 * Step 2 of the flow: choose the playbook that shapes the experience. Renders
 * the org's playbooks as a horizontal carousel of PlaybookCards, with a trailing
 * "Build your own" ghost card.
 */
export const PlaybookCarousel: React.FC<PlaybookCarouselProps> = ({
  selectedId,
  onSelect,
  onEditPlaybook,
  onCustomizePlaybook,
  onPlaybookDeleted,
  onBuildYourOwn,
  isLoading,
  playbooks,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { open } = useNotification();
  const { getOrganization, currentOrganization } = useSession();
  const { updatePlaybook, deletePlaybook } = usePlaybookRecord();

  // Preview opens the read-only details modal; Customize switches to the in-page
  // customize view (handled by the parent).
  const [previewPlaybook, setPreviewPlaybook] =
    useState<CarouselPlaybook | null>(null);

  // The ⋮ menu's two destinations, as one piece of state: only one of them is
  // ever open, and a union says so where two booleans could disagree.
  const [action, setAction] = useState<MenuAction | null>(null);
  // The modals animate closed, so they outlive `action` by a frame or two.
  // Reading the playbook straight off `action` blanked their title mid-fade;
  // this keeps the last one they were opened for. Adjusted during render
  // (React's documented pattern) rather than in an effect, so the title never
  // paints empty at all.
  const [shownAction, setShownAction] = useState<MenuAction | null>(null);
  if (action && action !== shownAction) setShownAction(action);
  const [isPending, setIsPending] = useState(false);

  // Only the current org's own playbooks are editable in place, so only they get
  // the ⋮ menu. Libertify's official ones belong to another organization.
  const isOwnPlaybook = (playbook: CarouselPlaybook) =>
    !!currentOrganization && playbook.organizationId === currentOrganization.id;

  const handleDelete = async () => {
    if (action?.kind !== "delete" || isPending) return;
    const { playbook } = action;
    setIsPending(true);
    try {
      await deletePlaybook(playbook.id);
      onPlaybookDeleted(playbook.id);
      setAction(null);
    } catch (error) {
      console.error("Error deleting playbook:", error);
      open?.({
        type: "error",
        message: t("createExperience.errors.playbookDeleteFailed"),
      });
    } finally {
      setIsPending(false);
    }
  };

  const handleRename = async (name: string, description: string | null) => {
    if (action?.kind !== "rename" || isPending) return;
    const { playbook } = action;
    setIsPending(true);
    try {
      await updatePlaybook(playbook.id, { name, description });
      setAction(null);
    } catch (error) {
      console.error("Error renaming playbook:", error);
      open?.({
        type: "error",
        message: t("createExperience.errors.playbookRenameFailed"),
      });
    } finally {
      setIsPending(false);
    }
  };

  const getPlaybookOwner = useCallback(
    (playbook: CarouselPlaybook) => {
      const playbookOrganization = getOrganization(playbook.organizationId);
      if (playbook.organizationId === LIBERTIFY_DEFAULT_PLAYBOOK_ORG_ID) {
        return t("pages.playbooks.by", {
          organizationName: "Libertify",
        });
      }
      return playbookOrganization?.name
        ? t("pages.playbooks.by", {
            organizationName: playbookOrganization.name,
          })
        : undefined;
    },
    [getOrganization, t]
  );

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <StateWrap>
        <Spinner size="lg" />
      </StateWrap>
    );
  } else {
    body = (
      <Slider role="group" aria-label={t("createExperience.step2.title")}>
        {playbooks.map((playbook) => (
          <Slide key={playbook.id}>
            <PlaybookCreationCard
              owner={getPlaybookOwner(playbook)}
              title={playbook.name}
              subtitle={playbook.description}
              gradient={playbook.gradient}
              selected={playbook.id === selectedId}
              onSelect={() => onSelect(playbook)}
              onPreview={() => setPreviewPlaybook(playbook)}
              onCustomize={() => onCustomizePlaybook(playbook)}
              menu={
                isOwnPlaybook(playbook) ? (
                  <PlaybookCardMenu
                    playbookName={playbook.name}
                    onEdit={() => onEditPlaybook(playbook)}
                    onRename={() => setAction({ kind: "rename", playbook })}
                    onDelete={() => setAction({ kind: "delete", playbook })}
                  />
                ) : undefined
              }
            />
          </Slide>
        ))}
        <GhostCard
          $token={token}
          type="button"
          aria-label={t("createExperience.step2.buildTitle")}
          onClick={onBuildYourOwn}
        >
          <GhostIcon>
            <PlusIcon size={26} weight="bold" />
          </GhostIcon>
          <GhostTitle $token={token}>
            {t("createExperience.step2.buildTitle")}
          </GhostTitle>
          <GhostDesc $token={token}>
            {t("createExperience.step2.buildDesc")}
          </GhostDesc>
        </GhostCard>
      </Slider>
    );
  }

  return (
    <Wrap>
      {/* Once for the whole carousel — each card's ⋮ menu shares the class. */}
      <PlaybookCardMenuStyles />
      <StepEyebrow $token={token}>
        {t("createExperience.step2.eyebrow")}
      </StepEyebrow>
      <Title $token={token}>{t("createExperience.step2.title")}</Title>
      {body}
      <PlaybookDetailsModal
        open={!!previewPlaybook}
        playbook={previewPlaybook}
        owner={previewPlaybook ? getPlaybookOwner(previewPlaybook) : undefined}
        selected={!!previewPlaybook && previewPlaybook.id === selectedId}
        onSelect={() => previewPlaybook && onSelect(previewPlaybook)}
        onClose={() => setPreviewPlaybook(null)}
      />
      <ConfirmRemovalModal
        open={action?.kind === "delete"}
        i18nKey="createExperience.deletePlaybookModal"
        name={shownAction?.playbook.name ?? ""}
        isPending={isPending}
        onCancel={() => setAction(null)}
        onConfirm={() => void handleDelete()}
      />
      <RenamePlaybookModal
        open={action?.kind === "rename"}
        currentName={shownAction?.playbook.name ?? ""}
        currentDescription={shownAction?.playbook.description}
        isSaving={isPending}
        onCancel={() => setAction(null)}
        onConfirm={(name, description) => void handleRename(name, description)}
      />
    </Wrap>
  );
};
